import React, { useState, useEffect, useCallback } from 'react';
// Les chiffres de synthèse suivent l'accent privé ; les courbes gardent leurs séries colorées.
import { PRIVATE_PRIMARY_COLOR } from '../theme/privateTheme';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  DatePicker, 
  Button, 
  Statistic, 
  Table, 
  Spin
} from 'antd';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import {
  StockOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  RiseOutlined,
  FallOutlined,
  RobotOutlined
} from '@ant-design/icons';
import AIAnalysisModal from '../components/analytics/AIAnalysisModal';
import api from '../utils/api';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/fr_FR';
import { getDeviseActive } from '../utils/devise';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Hook simple pour détecter la taille de l'écran
const useBreakpoint = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  // État pour contrôler l'affichage du modal d'analyse IA
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [analytics, setAnalytics] = useState({
    overview: {},
    revenueEvolution: [],
    ordersEvolution: [],
    topProducts: [],
    topCustomers: [],
    recentOrders: [],
    profitability: null,
    profitEvolution: [],
    deliveryRevenue: [],
  });

  const isMobile = useBreakpoint();

  // Centralise le chargement afin que tous les widgets, y compris les remises des livreurs, utilisent exactement la même période.
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0].startOf('day').toDate();
      const endDate = dateRange[1].endOf('day').toDate();
      
      const [
        overviewRes,
        revenueRes,
        ordersRes,
        topProductsRes,
        topCustomersRes,
        recentOrdersRes,
        profitabilityRes,
        profitEvolutionRes,
        deliveryRevenueRes,
      ] = await Promise.all([
        api.get('/analytics/overview', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/revenue-evolution', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/orders-evolution', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/top-products', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/top-customers', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/recent-orders', { params: { limit: 10 } }),
        api.get('/analytics/profitability', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/profit-evolution', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
        api.get('/analytics/delivery-person-revenue', { params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() } }),
      ]);

      setAnalytics({
        overview: overviewRes.data,
        revenueEvolution: revenueRes.data,
        ordersEvolution: ordersRes.data,
        topProducts: topProductsRes.data,
        topCustomers: topCustomersRes.data,
        recentOrders: recentOrdersRes.data,
        profitability: profitabilityRes.data,
        profitEvolution: profitEvolutionRes.data,
        deliveryRevenue: deliveryRevenueRes.data,
      });

    } catch (error) {
      console.error('Erreur lors du chargement des analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Recharge automatiquement les indicateurs lorsque l'utilisateur applique un raccourci ou modifie les dates.
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      fetchAnalytics();
    }
  }, [fetchAnalytics]);

  // Met à jour la période depuis les raccourcis visibles, puis déclenche le rechargement via l'effet lié à dateRange.
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const now = dayjs();
    let startDate;
    let endDate = now;
    switch (period) {
      case 'today': startDate = now.startOf('day'); endDate = now.endOf('day'); break;
      case 'yesterday': startDate = now.subtract(1, 'day').startOf('day'); endDate = now.subtract(1, 'day').endOf('day'); break;
      case '7d': startDate = now.subtract(7, 'day'); break;
      case '30d': startDate = now.subtract(30, 'day'); break;
      case '360d': startDate = now.subtract(360, 'day'); break;
      default: startDate = now.subtract(30, 'day');
    }
    setDateRange([startDate, endDate]);
  };

  // Enregistre une plage choisie manuellement et désactive visuellement les raccourcis de période.
  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setSelectedPeriod('custom');
    }
  };

  const chartColors = { primary: '#3b82f6', secondary: '#06b6d4' };
  const formatCurrency = (value) => `${value?.toLocaleString() ?? 0} ${getDeviseActive()}`;
  const formatNumber = (value) => value?.toLocaleString() ?? 0;
  const formatDateSafe = (date, formatString = 'DD/MM/YYYY HH:mm') => {
    try {
      return date ? dayjs(date).format(formatString) : 'N/A';
    } catch {
      return 'Date invalide';
    }
  };

  const renderOrderNumber = (text) => <span className="font-medium text-gray-700">#{text}</span>;
  const renderAmount = (value) => (
    <span className="font-medium">
      {isMobile ? `${(value / 1000).toFixed(0)}k` : formatCurrency(value)}
    </span>
  );
  const renderStatus = (status) => (
    <span className={`px-2 py-1 text-xs rounded ${
      status === 'completed' ? 'bg-green-100 text-green-800' :
      status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
      'bg-gray-100 text-gray-800'
    }`}>
      {status === 'completed' ? 'Terminée' : status === 'pending' ? 'En cours' : status}
    </span>
  );

  const columns = {
    recentOrders: [
      { title: 'N°', dataIndex: 'order_number', key: 'order_number', render: renderOrderNumber },
      { title: 'Client', dataIndex: 'customer_name', key: 'customer_name', responsive: ['sm'] },
      { title: 'Montant', dataIndex: 'total_amount', key: 'total_amount', render: renderAmount },
      { title: 'Statut', dataIndex: 'status', key: 'status', render: renderStatus },
      { title: 'Date', dataIndex: 'created_at', key: 'created_at', responsive: ['md'], render: (date) => formatDateSafe(date, 'DD/MM/YY HH:mm') },
    ],
    topProducts: [
      { title: 'Produit', dataIndex: 'product_name', key: 'product_name' },
      { title: isMobile ? 'Qté' : 'Qté vendue', dataIndex: 'total_quantity', key: 'total_quantity', render: (v) => formatNumber(v) },
    ],
    topCustomers: [
      { title: 'Client', dataIndex: 'customer_name', key: 'customer_name' },
      { title: isMobile ? 'Cmd' : 'Commandes', dataIndex: 'total_orders', key: 'total_orders', render: (v) => formatNumber(v) },
      { title: 'Total', dataIndex: 'total_spent', key: 'total_spent', render: renderAmount },
    ],
    deliveryRevenue: [
      { title: 'Livreur', dataIndex: 'deliveryPersonName', key: 'deliveryPersonName' },
      { title: 'Commandes', dataIndex: 'ordersCount', key: 'ordersCount', align: 'center', render: formatNumber },
      { title: 'Frais de livraison', dataIndex: 'shippingFees', key: 'shippingFees', responsive: ['md'], render: renderAmount },
      { title: 'À remettre', dataIndex: 'amountToRemit', key: 'amountToRemit', render: renderAmount },
    ],
  };

  const cardClass = "shadow-sm border-0 bg-white sm:bg-white/70 sm:backdrop-blur-md rounded-xl";

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <header className="mb-4 sm:mb-6">
        <Title level={2} className="text-2xl sm:text-3xl font-bold text-gray-800">
          Analytics Business
        </Title>
        <Text className="text-gray-500">
          Tableau de bord des performances de votre boutique.
        </Text>
      </header>

      <Card className={`${cardClass} mb-4 sm:mb-6`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'today', label: "Aujourd'hui" },
                { value: 'yesterday', label: 'Hier' },
                { value: '7d', label: '7 derniers jours' },
                { value: '30d', label: '30 derniers jours' },
                { value: '360d', label: '360 derniers jours' },
              ].map((period) => (
                <Button
                  key={period.value}
                  type={selectedPeriod === period.value ? 'primary' : 'default'}
                  onClick={() => handlePeriodChange(period.value)}
                >
                  {period.label}
                </Button>
              ))}
            </div>
            <RangePicker
              locale={locale}
              value={dateRange}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              className="w-full sm:w-auto"
              size={isMobile ? 'middle' : 'large'}
            />
          </div>
          <Button 
            type="primary" 
            onClick={fetchAnalytics}
            loading={loading}
            size={isMobile ? 'middle' : 'large'}
            className="w-full sm:w-auto"
          >
            Actualiser
          </Button>
        </div>
      </Card>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
          {[
            { title: 'Chiffre d\'affaires', dataKey: 'totalRevenue', prefix: <DollarCircleOutlined />, color: '#059669', formatter: formatCurrency },
            { title: 'Commandes', dataKey: 'totalOrders', prefix: <ShoppingCartOutlined />, color: PRIVATE_PRIMARY_COLOR, formatter: formatNumber },
            { title: 'Clients actifs', dataKey: 'activeCustomers', prefix: <UserOutlined />, color: PRIVATE_PRIMARY_COLOR, formatter: formatNumber },
            { title: 'Panier moyen', dataKey: 'averageOrderValue', prefix: <StockOutlined />, color: '#f59e0b', formatter: formatCurrency },
          ].map(stat => (
            <Col key={stat.dataKey} xs={12} sm={12} lg={6}>
              <Card className={cardClass}>
                <Statistic
                  title={<span className="text-sm font-medium text-gray-500">{stat.title}</span>}
                  value={analytics.overview[stat.dataKey] || 0}
                  formatter={stat.formatter}
                  prefix={React.cloneElement(stat.prefix, { style: { color: stat.color } })}
                  valueStyle={{ color: stat.color, fontSize: isMobile ? '20px' : '28px', fontWeight: '600' }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Section Rentabilité */}
        {analytics.profitability && (
          <>
            {/* En-tête avec bouton Analyse IA */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <Title level={3} className="text-xl font-bold text-gray-800 mb-0">
                  💰 Rentabilité & Profits
                </Title>
                <Text className="text-gray-500">
                  Vision claire sur vos gains et vos dépenses
                </Text>
              </div>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={() => setAiModalVisible(true)}
                className="self-start sm:self-auto"
              >
                Analyse IA
              </Button>
            </div>

            <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
              <Col xs={24} sm={12} lg={6}>
                <Card className={cardClass}>
                  <Statistic
                    title={<span className="text-sm font-medium text-gray-500">Revenus</span>}
                    value={analytics.profitability.totalRevenue || 0}
                    formatter={formatCurrency}
                    prefix={<DollarCircleOutlined style={{ color: '#059669' }} />}
                    valueStyle={{ color: '#059669', fontSize: isMobile ? '20px' : '28px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className={cardClass}>
                  <Statistic
                    title={<span className="text-sm font-medium text-gray-500">Dépenses</span>}
                    value={analytics.profitability.totalExpenses || 0}
                    formatter={formatCurrency}
                    prefix={<WalletOutlined style={{ color: '#ef4444' }} />}
                    valueStyle={{ color: '#ef4444', fontSize: isMobile ? '20px' : '28px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className={cardClass}>
                  <Statistic
                    title={<span className="text-sm font-medium text-gray-500">Profit Net</span>}
                    value={analytics.profitability.netProfit || 0}
                    formatter={formatCurrency}
                    prefix={analytics.profitability.netProfit >= 0 ? 
                      <RiseOutlined style={{ color: '#10b981' }} /> : 
                      <FallOutlined style={{ color: '#ef4444' }} />
                    }
                    valueStyle={{ 
                      color: analytics.profitability.netProfit >= 0 ? '#10b981' : '#ef4444', 
                      fontSize: isMobile ? '20px' : '28px', 
                      fontWeight: '600' 
                    }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card className={cardClass}>
                  <Statistic
                    title={<span className="text-sm font-medium text-gray-500">Marge Bénéficiaire</span>}
                    value={analytics.profitability.profitMargin || 0}
                    suffix="%"
                    precision={2}
                    prefix={analytics.profitability.profitMargin >= 0 ? 
                      <RiseOutlined style={{ color: '#8b5cf6' }} /> : 
                      <FallOutlined style={{ color: '#ef4444' }} />
                    }
                    valueStyle={{ 
                      color: analytics.profitability.profitMargin >= 20 ? '#10b981' : 
                             analytics.profitability.profitMargin >= 10 ? '#f59e0b' : '#ef4444', 
                      fontSize: isMobile ? '20px' : '28px', 
                      fontWeight: '600' 
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Alerte si dépenses > revenus */}
            {analytics.profitability.netProfit < 0 && (
              <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
                <Col xs={24}>
                  <Card className={cardClass} style={{ borderLeft: '4px solid #ef4444' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FallOutlined style={{ fontSize: '24px', color: '#ef4444' }} />
                      <div>
                        <Title level={5} style={{ margin: 0, color: '#ef4444' }}>
                          Attention : Vos dépenses dépassent vos revenus !
                        </Title>
                        <Text>
                          Vous avez une perte de {formatCurrency(Math.abs(analytics.profitability.netProfit))}. 
                          Pensez à optimiser vos dépenses ou augmenter vos ventes.
                        </Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Graphique d'évolution du profit */}
            <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
              <Col xs={24}>
                <Card title="Évolution : Revenus vs Dépenses vs Profit" className={`${cardClass} h-96`}>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={analytics.profitEvolution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }} 
                        stroke="#6b7280"
                        tickFormatter={(date) => formatDateSafe(date, 'DD/MM')}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }} 
                        stroke="#6b7280" 
                        tickFormatter={(v) => isMobile ? `${(v/1000).toFixed(0)}k` : formatCurrency(v)} 
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          formatCurrency(value), 
                          name === 'revenue' ? 'Revenus' : 
                          name === 'expenses' ? 'Dépenses' : 'Profit'
                        ]} 
                        labelFormatter={(date) => formatDateSafe(date, 'DD/MM/YYYY')}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue" 
                        stroke="#059669" 
                        strokeWidth={2}
                        name="revenue"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="expenses" 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        name="expenses"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="profit" 
                        stroke="#8b5cf6" 
                        strokeWidth={3}
                        name="profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
          <Col xs={24}>
            <Card
              title="Montants à remettre par livreur"
              extra={<Text type="secondary">Commandes finalisées de la période</Text>}
              className={cardClass}
            >
              <Table
                dataSource={analytics.deliveryRevenue}
                columns={columns.deliveryRevenue}
                pagination={false}
                rowKey="deliveryPersonId"
                locale={{ emptyText: 'Aucune commande finalisée attribuée à un livreur sur cette période.' }}
                size={isMobile ? 'small' : 'middle'}
                summary={(rows) => {
                  // Additionne les lignes déjà filtrées afin de rendre visible la remise totale de la période.
                  const totalToRemit = rows.reduce((total, row) => total + (row.amountToRemit || 0), 0);
                  return (
                    <Table.Summary.Row>
                      {/* La colonne « Frais de livraison » est masquée sous md : le libellé couvre alors 2 colonnes au lieu de 3 */}
                      <Table.Summary.Cell index={0} colSpan={isMobile ? 2 : 3}>
                        <Text strong>Total à remettre</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={isMobile ? 2 : 3}>
                        <Text strong>{formatCurrency(totalToRemit)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
              <Text type="secondary" className="block mt-3 text-xs">
                Le montant à remettre exclut les frais de livraison, qui restent attribués au livreur.
              </Text>
            </Card>
          </Col>
        </Row>


        <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
          <Col xs={24} lg={12}>
            <Card title="Évolution du chiffre d'affaires" className={`${cardClass} h-72 sm:h-96`}>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <AreaChart data={analytics.revenueEvolution}>
                  <defs><linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.8}/><stop offset="95%" stopColor={chartColors.primary} stopOpacity={0.1}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" tickFormatter={(v) => isMobile ? `${(v/1000).toFixed(0)}k` : formatCurrency(v)} />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'CA']} labelFormatter={(date) => formatDateSafe(date, 'DD/MM/YYYY')} />
                  <Area type="monotone" dataKey="total_revenue" stroke={chartColors.primary} fill="url(#revGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Évolution des commandes" className={`${cardClass} h-72 sm:h-96`}>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <LineChart data={analytics.ordersEvolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <Tooltip formatter={(value) => [formatNumber(value), 'Commandes']} labelFormatter={(date) => formatDateSafe(date, 'DD/MM/YYYY')} />
                  <Line type="monotone" dataKey="total_orders" stroke={chartColors.secondary} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-4 sm:mb-6">
          <Col xs={24} lg={12}>
            <Card title="🏆 Top Produits" className={cardClass}>
              <Table dataSource={analytics.topProducts} columns={columns.topProducts} pagination={false} size="small" rowKey="product_id" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="⭐ Top Clients" className={cardClass}>
              <Table dataSource={analytics.topCustomers} columns={columns.topCustomers} pagination={false} size="small" rowKey="customer_id" />
            </Card>
          </Col>
        </Row>

        {/* <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="🕒 Commandes récentes" className={cardClass}>
              <Table dataSource={analytics.recentOrders} columns={columns.recentOrders} pagination={{ pageSize: 5 }} rowKey="id" size={isMobile ? "small" : "middle"} />
            </Card>
          </Col>
        </Row> */}
      </Spin>

      {/* Modal d'analyse IA de la rentabilité */}
      <AIAnalysisModal
        visible={aiModalVisible}
        onCancel={() => setAiModalVisible(false)}
        dateRange={dateRange}
      />
    </div>
  );
};

export default Analytics;
